import React, { useCallback, useContext, useEffect } from 'react'
import '../App.css'
import { useIntl } from 'react-intl'
import styles from '../styles'
import Menu from '../components/menu/Menu'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { Security } from '@okta/okta-react'
import { useMediaQuery } from 'react-responsive'
import { useDispatch, useSelector } from 'react-redux'
import { saveUser } from '../modules/reducers/user/actions'
import { UserModel } from '../types/user/userModel'
import fetchFactory from '../utils/fetchFactory'
import errorCatcher from '../utils/errorCatcher'
import SnackBar from '../components/snackBar/SnackBar'
import { Helmet } from 'react-helmet'
import { useCookies } from 'react-cookie'
import TopAlert from '../components/topAlert/TopAlert'
import Root from './Root'
import RequestModal from '../components/RequestModal/RequestModal'
import { ContractsModel } from '../types/contracts/contractsModel'
import localStorageEnum from '../enums/localStorageEnum'
import { clearStorage } from '../api/logout'
import Loader from '../components/loader/Loader'
import YFPopIn from '../modules/yf_popin/YFPopIn'
import Response from '../modules/requests/response/Response'
import { checkNbOfMonthsSinceLastDisplay, convertContractList, getLatestDateRelancePrelevement } from '../modules/contracts/contractMapper'
import AppFooter from '../modules/footer/AppFooter'
import { Route, useLocation } from 'react-router'
import { convertUser } from '../types/user/userMapper'
import { getAuthClient, TOKEN_RENEWED } from '../utils/authClient'
import { RootState } from '../modules/reducers'
import links from '../enums/linksEnum'
import Cgu from '../modules/footer/Cgu'
import ServiceModal from '../modules/services/components/modal/ServiceModal'
import Preview from '../modules/preview/Preview'
import SubscriptionPrelevementModal from '../components/prelevementModal/SubscriptionPrelevementModal'
import { PrelevementModalModel } from '../types/prelevement/prelevementModalModel'
import { PRL_AUTO, propertyType } from '../enums/contractsEnum'
import { saveRequests } from '../modules/reducers/requests/actions'
import { convertRequestList } from '../modules/requests/requestMapper'
import { checkTokenValidity } from './api/checkTokenValidity'
import { ACCESS_TOKEN_STORAGE_KEY } from '@okta/okta-auth-js'
import { saveContracts } from '../modules/reducers/contracts/actions'
import { getCGU } from '../modules/cgu/api/getCGU'
import { displayModal } from '../modules/reducers/modal/actions'
import { modalTypes } from '../enums/modalTypeEnum'
import PopinCGU from '../modules/cgu/PopinCGU'
import { saveDossier } from '../modules/reducers/dossier/actions'
import { displaySnackbar } from '../modules/reducers/snackBar/actions'
import YFPopInEcDesactive from '../modules/yf_popin/YFPopInEcDesactive'
import { phaseEnum } from '../enums/phaseEnum'
import { saveAssurance } from '../modules/reducers/assurance/actions'
import { canDisplayAssuranceAlert, isNouveauLocataire } from '../components/topAlert/utils/assuranceUtils'
import { RequestItemModel } from '../modules/requests/requestsModel'
import { youfirstEnvEnum } from '../enums/youfirstEnvEnum'
import { LangContext } from '../components/langContext/langContext'

const useStyles = makeStyles((theme) => ({
		...styles(theme),
		containerStyle: {
			background: theme.palette.blue['5'],
			display: 'flex',
			flexDirection: 'column'
		},
		appLargeScreenStyle: {
			width: '80%',
			margin: '0 auto',
			maxWidth: 1100,
			paddingBottom: 150
		},
		appSmallScreenStyle: {
			width: '90%',
			margin: '0 auto',
			paddingBottom: 80
		},
		paddingLargeScreenStyle: {
			paddingTop: 140
		},
		paddingSmallScreenStyle: {
			paddingTop: 85
		},
		loaderContainerStyle: {
			height: '100vh'
		},
		appFlex: {
			flex: 1
		}
	}
))

interface ModelAccessToken {
	accessToken: string
	authorizeUrl: string
	expiresAt: number
	scopes: string[]
	tokenType: string
	userinfoUrl: string
	value: string
}

const LAST_URL: string = 'lastUrl'
const FETCH_DATA: string = 'fetch_data'
const REQUESTS_ERROR = 'get_requests'
const GET_USER = 'get_user'

const App = () => {
	const intl = useIntl()
	const classes = useStyles()
	const location = useLocation()

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)
	const authClient = getAuthClient()

	const [localUser, setLocalUser] = React.useState(localStorage.getItem(localStorageEnum.user))
	const [loading, setLoading] = React.useState<boolean>(true)
	const prelevement: PrelevementModalModel = useSelector((state: RootState) => state.prelevement)
	const contracts: ContractsModel[] = useSelector((state: RootState) => state.contracts)
	const dispatch = useDispatch()
	const [hasFetchContracts, setHasFetchContracts] = React.useState<boolean>(false)
	const [userEspaceClientDesactive, setUserEspaceClientDesactive] = React.useState<boolean>(false)
	const requests: RequestItemModel[] = useSelector((state: RootState) => state.requests)
	const { switchLang } = useContext(LangContext)

	const [cookies, setCookie, removeCookies] = useCookies([ACCESS_TOKEN_STORAGE_KEY, LAST_URL])
	const accessTokenFromCookies: ModelAccessToken = cookies[ACCESS_TOKEN_STORAGE_KEY]

	const [isTopAlertOpen, setIsTopAlertOpen] = React.useState<boolean>(true)

	const containerClass: ClassValue = classNames(
		'App',
		classes.containerStyle
	)

	const displayAlertPrelevement = (contracts.filter(contract => contract.typeDePaiement !== PRL_AUTO).length > 0) && location.pathname === links.contracts && checkNbOfMonthsSinceLastDisplay(getLatestDateRelancePrelevement(contracts).dateRelancePrelevement)

	const displayAlertAssuranceFinie = (contracts.filter(contract =>
		contract.phase === phaseEnum.exploitation &&
		contract.type === propertyType.apartment &&
		(contract.dateFinAssurance === null || new Date() >= contract.dateFinAssurance)
	).length > 0) && canDisplayAssuranceAlert(requests) && !isNouveauLocataire(contracts)

	if (displayAlertAssuranceFinie) {
		dispatch(saveAssurance({
			assuranceFinie: true,
			finAssuranceProche: false
		}))
	}

	const displayAlertFinAssuranceProche = (contracts.filter(contract =>
		contract.phase === phaseEnum.exploitation &&
		contract.type === propertyType.apartment &&
		contract.dateFinAssurance &&
		new Date(contract.dateFinAssurance) > new Date() &&
		new Date(contract.dateFinAssurance) <= new Date(new Date().setMonth((new Date()).getMonth() + 1))
	).length > 0) && canDisplayAssuranceAlert(requests) && !isNouveauLocataire(contracts)

	if (displayAlertFinAssuranceProche) {
		dispatch(saveAssurance({
			assuranceFinie: false,
			finAssuranceProche: true
		}))
	}

	const appClass: ClassValue = classNames(
		classes.appFlex,
		{
			[classes.appLargeScreenStyle]: isLargeScreen,
			[classes.appSmallScreenStyle]: !isLargeScreen,
			[classes.paddingLargeScreenStyle]: isLargeScreen && (!isTopAlertOpen && !(prelevement.subscribePrelevementModal && displayAlertPrelevement) && !displayAlertAssuranceFinie && !displayAlertFinAssuranceProche),
			[classes.paddingSmallScreenStyle]: !isLargeScreen && (!isTopAlertOpen && !(prelevement.subscribePrelevementModal && displayAlertPrelevement) && !displayAlertAssuranceFinie && !displayAlertFinAssuranceProche)
		}
	)

	const fetchData = useCallback(
		() => {
			return fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/utilisateur/getInformations`)
				.then(
					(data: any) => {
						dispatch(
							saveContracts(convertContractList(data.contracts))
						)
						if (data.dossier) {
							dispatch(saveDossier({
								...data.dossier,
								eft_type_de_candidature: data.dossier.eft_type_de_candidature.toString()
							}))
						}
						localStorage.setItem(localStorageEnum.residences, data.residences)
						localStorage.setItem(localStorageEnum.buildings, data.buildings)
						localStorage.setItem(localStorageEnum.mainBuilding, data.mainBuilding)
						setHasFetchContracts(true)
						setUserEspaceClientDesactive(data.espaceClientDesactive)

						removeCookies(LAST_URL, {
							domain: JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').cookie_domain || '',
							path: '/'
						})
					}
				)
				.catch(
					(error: any) => {
						dispatch(
							displaySnackbar(
								{
									id: FETCH_DATA,
									message: intl.formatMessage(
										{
											id: 'profile.errors.get_user_data',
											defaultMessage: 'Une erreur est survenue lors de la récupération des données utilisateurs',
											description: 'Error message'
										}
									),
									open: true,
									hideIcon: true
								}
							)
						)
					}
				)
				.finally(
					() => {
						setLoading(false)
					}
				)
		}, [dispatch, removeCookies, intl]
	)

	const fetchRequests = useCallback
	((user: UserModel) => {
			return fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/demande/getRequestList${user.dateDeModification ? '?date=' + user.dateDeModification : ''}`)
				.then(
					(response: any) => {
						if (response && response.value) {
							dispatch(
								saveRequests(convertRequestList(response.value))
							)
						}
					}
				)
				.catch(
					(error) => {
						dispatch(
							errorCatcher(error, REQUESTS_ERROR)
						)
					}
				)
		}, [dispatch]
	)

	const setLastUrl = useCallback(
		(pathname: string) => {
			return new Promise(
				(resolve) => {
					setCookie(LAST_URL, pathname,
						{
							domain: JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').cookie_domain || '',
							path: '/',
							secure: true
						}
					)

					resolve(true)
				}
			)
		}, [setCookie]
	)

	const saveTokens = useCallback(
		() => {
			return new Promise(
				(resolve) => {
					if (accessTokenFromCookies) {
						checkTokenValidity(accessTokenFromCookies.value)
							.then(
								() => {
									authClient.tokenManager.add(ACCESS_TOKEN_STORAGE_KEY, accessTokenFromCookies)

									authClient.tokenManager.on(TOKEN_RENEWED, (key: string, newToken: ModelAccessToken) => {
										setCookie(key, newToken, {
											path: '/',
											domain: JSON.parse(localStorage.getItem('commonUri') || '').cookie_domain || '',
											secure: true
										})
									})

									resolve()
								}
							)
							.catch(
								() => {
									clearStorage(removeCookies)

									setLastUrl(location.pathname)
										.then(
											() => window.location.href = JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').login_uri || ''
										)
								}
							)
					} else {
						setLastUrl(location.pathname)
							.then(
								() => window.location.href = JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').login_uri || ''
							)
					}
				}
			)
		}, [accessTokenFromCookies, authClient.tokenManager, location.pathname, removeCookies, setCookie, setLastUrl]
	)

	useEffect(
		() => {
			switchLang(localStorage.getItem(localStorageEnum.language) || (window.navigator.language).substring(0, 2))
			if (location.pathname !== links.cgu && !location.pathname.includes(links.preview) && contracts.length === 0 && !hasFetchContracts) {
				saveTokens()
					.then(
						() => {
							if (!localUser) {
								// DR-3394.
								fetchFactory('/api/login/utilisateur/redirection')
									.then(
										(response: any) => {
											if (![youfirstEnvEnum.CAMPUS, youfirstEnvEnum.RESIDENCE].includes(response)) {
												window.location.href = JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').login_uri || ''
											} else {
												fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/utilisateur/getUser`)
													.then(
														(user: any) => {
															const newUser = convertUser(user[0])
															dispatch(
																saveUser(newUser)
															)
															localStorage.setItem(localStorageEnum.user, JSON.stringify(newUser))
															setLocalUser(JSON.stringify(newUser))
															fetchRequests(newUser)
														}
													)
													.catch(
														(error: any) => {
															dispatch(
																errorCatcher(error, GET_USER)
															)
															setLastUrl(location.pathname)
																.then(
																	() => window.location.href = JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').login_uri || ''
																)
														}
													)
											}
										}
									)
									.catch(
										(error: any) => {
											dispatch(
												errorCatcher(error, GET_USER)
											)
											setLastUrl(location.pathname)
												.then(
													() => window.location.href = JSON.parse(localStorage.getItem(localStorageEnum.commonUri) || '').login_uri || ''
												)
										}
									)
							} else {
								dispatch(
									saveUser(JSON.parse(localUser))
								)
								fetchRequests(JSON.parse(localUser))
							}

							// Récupération CGU.
							getCGU(intl.locale, `ec_${process.env.REACT_APP_TARGET}`)
								.then((data: any) => {
									if (data.cgu) {
										dispatch(displayModal(modalTypes.cguModal, { cgu_content: data.cgu }))
									}
								})

							return fetchData()
						}
					)
			} else {
				setLoading(false)
			}
		}, [dispatch, fetchData, localUser, location.pathname, setLastUrl, saveTokens, contracts, fetchRequests, hasFetchContracts, intl.locale, switchLang]
	)

	// Suppression localStorage de Acqui Resi
	useEffect(
		() => {
			localStorage.removeItem(localStorageEnum.selectedLots)
		}, []
	)

	if (loading) {
		return (
			<>
				<Helmet>
					<title>{process.env.REACT_APP_TARGET === 'residence' ? 'Espace Client YouFirst Residence' : 'Espace Client YouFirst Campus'}</title>
				</Helmet>

				<div className={classes.loaderContainerStyle}>
					<Loader />
				</div>
			</>
		)
	}

	if (location.pathname && location.pathname.includes(links.preview)) {
		return (
				<Route path={links.preview} component={Preview} />
		)
	}

	return (
		<>
			<Helmet>
				<meta http-equiv="content-language" content={intl.locale} />
				<title>{process.env.REACT_APP_TARGET === 'residence' ? 'Espace Client YouFirst Residence' : 'Espace Client YouFirst Campus'}</title>
			</Helmet>

			<div
				id="outer-container"
				className={containerClass}
			>
				{
					location.pathname !== links.cgu && !location.pathname.includes(links.preview) && (
						<Security
							oktaAuth={authClient}
							restoreOriginalUri={() => {
							}}
						>
							<header className="App-header">
								<Menu intl={intl} />
								<TopAlert
									isTopAlertOpen={isTopAlertOpen}
									onTopAlertClosed={() => setIsTopAlertOpen(false)}
								/>
							</header>

							<div
								id="App"
								className={appClass}
							>
								{
									userEspaceClientDesactive ? <YFPopInEcDesactive /> : <Root />
								}
							</div>

							<AppFooter />

							<SnackBar />
							<RequestModal />
							<PopinCGU />
							<Response />
							<ServiceModal />

							{
								localStorage.getItem(localStorageEnum.mainBuilding) && <YFPopIn />
							}

							<SubscriptionPrelevementModal />
						</Security>
					)
				}

				{
					location.pathname === links.cgu && (
						<Route path={links.cgu} component={Cgu} />
					)
				}
			</div>
		</>
	)
}

export default App
